/**
 * 검색결과 종합 뷰 데이터 헬퍼 — 순수 함수 (jest 테스트 대상, DOM 무의존).
 *
 * buildTariffCompareRows: tariff-search items(잎 10자리) + heading_matches(호·소호 표제)를
 *   CLHS 관세율표 검색결과처럼 코드 오름차순 한 테이블로 병합.
 *   잎 행의 기본세율/탄력·양허 표시는 HeadingGrid 잎 행 규칙과 동일
 *   (A=기본, FTA 제외 일반세율 대표 1개 + " …", APTA 우선).
 * splitPrecedentsBySource: precedents-search items → 분류사례(관세청) vs
 *   결정례·사전심사(pre_ruling·clip_foreign) 분리 — ②/⑤ 섹션이 fetch 1회 공유.
 */
import { categorizeRate } from "./rateCodeTaxonomy";

const CAT_ORDER = [
  "wto",
  "apta",
  "quota_alloc",
  "quota_agri",
  "safeguard",
  "dumping",
  "adjustment",
  "intl_coop",
  "preference_ldc",
  "preference_nk",
  "preference_gsp",
  "wto_related",
  "misc",
];

// "08.04 - 대추야자…"(한글·대시) / "08.04   Dates…"(영문·대시 없음, 공백구분) 코드 프리픽스 제거
function stripCodePrefix(text) {
  return String(text || "")
    .replace(/^[\d.]*\s*[-–]\s*/, "") // 코드 + 대시 (한글 호해설 줄)
    .replace(/^\d+\.\d+\s+/, "");     // 대시 없는 코드 + 공백 (영문 줄)
}

export function buildTariffCompareRows(items, headingMatches) {
  const rows = [];
  for (const h of headingMatches || []) {
    if (!h || !h.code) continue;
    rows.push({
      type: h.level === "heading" ? "heading" : "subheading",
      key: `${h.level}-${h.code}`,
      sortKey: String(h.code),
      hs4: h.hs4,
      code: String(h.code),
      label: stripCodePrefix(h.line_ko || h.line_en),
      labelEn: h.line_ko ? stripCodePrefix(h.line_en) : "",
    });
  }
  for (const it of items || []) {
    const digits = String((it && it.hsk) || "").replace(/\D/g, "");
    if (digits.length < 10) continue;
    const rates = it.rates || {};
    const basicRaw = rates.A;
    // 탄력·양허 — HeadingGrid 잎 행과 동일: FTA/기본 제외, 카테고리순 정렬,
    // 대표 1개(APTA 우선) + 더 있으면 " …"
    const tanParts = [];
    for (const [code, val] of Object.entries(rates)) {
      const info = categorizeRate(code);
      if (info.category === "basic" || info.isFta) continue;
      tanParts.push({ code, display: val != null ? `${code} ${val}` : code });
    }
    tanParts.sort((a, b) => {
      const ia = CAT_ORDER.indexOf(categorizeRate(a.code).category);
      const ib = CAT_ORDER.indexOf(categorizeRate(b.code).category);
      const oa = ia === -1 ? 99 : ia;
      const ob = ib === -1 ? 99 : ib;
      if (oa !== ob) return oa - ob;
      return a.code.localeCompare(b.code);
    });
    const rep =
      tanParts.find((p) => categorizeRate(p.code).category === "apta") ||
      tanParts[0];
    rows.push({
      type: "leaf",
      key: digits,
      sortKey: digits,
      hs4: digits.slice(0, 4),
      sub2: digits.slice(4, 6),
      tail4: digits.slice(6),
      hsk: digits,
      ko: it.desc_ko || "",
      en: it.desc || "",
      basicRate: basicRaw != null ? String(basicRaw) : "",
      basicRateNum:
        basicRaw != null && !Number.isNaN(Number(basicRaw))
          ? Number(basicRaw)
          : null,
      tan: rep ? rep.display + (tanParts.length > 1 ? " …" : "") : "",
      hasImportReq: !!it.has_import_req,
      hasExportReq: !!it.has_export_req,
    });
  }
  rows.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  // 최저 기본세율 ★ — 수치 있는 잎 2개 이상 + 전 잎 동률이 아닐 때만
  const nums = rows
    .filter((r) => r.type === "leaf" && r.basicRateNum != null)
    .map((r) => r.basicRateNum);
  if (nums.length >= 2) {
    const min = Math.min(...nums);
    const max = Math.max(...nums);
    if (min !== max) {
      for (const r of rows) {
        if (r.type === "leaf" && r.basicRateNum === min) r.isMinRate = true;
      }
    }
  }
  return rows;
}

export function splitPrecedentsBySource(items) {
  const cases = [];
  const rulings = [];
  for (const it of items || []) {
    if (it && (it.source === "pre_ruling" || it.source === "clip_foreign")) {
      rulings.push(it);
    } else if (it) {
      cases.push(it);
    }
  }
  return { cases, rulings };
}

/**
 * Decide whether fast CLHS/CLIP search should escalate to public-web product research.
 * Generic nouns and HS codes stay local. Only an explicit commercial signature (model/quantity/
 * pack unit) with no tariff hit auto-runs grounding. Every non-code search still gets an opt-in
 * CTA so the user can deliberately compare the local result with public-web product evidence.
 */
export function decideProductGrounding({ q, tariffCount, localLoading }) {
  if (localLoading || tariffCount == null) return "off";
  const query = String(q || "").replace(/\s+/g, " ").trim();
  if (!query) return "off";
  const compactDigits = query.replace(/\D/g, "");
  const looksLikeHsCode = /^\s*\d{4}(?:[.\s-]?\d{2})?(?:[.\s-]?\d{4})?\s*$/.test(query)
    && [4, 6, 10].includes(compactDigits.length);
  if (looksLikeHsCode) return "off";

  const hasQuantityUnit = /\d+(?:[.,]\d+)?\s*(?:ea|pcs?|ml|l|g|kg|mm|cm|m|개|매|병|포|팩|세트)\b/i.test(query);
  const hasModelToken = /(?:[a-z]+[-_/]?[0-9]+|[0-9]+[-_/]?[a-z]+)/i.test(query);
  const hasPackWord = /(?:\b(?:set|pack|kit)\b|세트|묶음|패키지)/i.test(query);
  if (tariffCount === 0 && (hasQuantityUnit || hasModelToken || hasPackWord)) return "auto";

  return "manual";
}

// 주·해설/법령 desc 의 앵커·태그 제거 후 공백 정리·절단(보고서 근거 텍스트용).
function cleanText(s, max) {
  const t = String(s || "")
    .replace(/<a\s[^>]*>([^<]*)<\/a>/g, "$1")
    .replace(/<\/?[a-z][^>]*>/gi, "")
    .replace(/\s+/g, " ")
    .trim();
  return max && t.length > max ? t.slice(0, max) + "…" : t;
}

/**
 * 검색결과 종합 뷰 → 검토보고서 구조화 데이터(ClassifyReportModal reportData).
 *   관세율표 검색 순위는 분류 결론이 아니다. 명시적으로 선택된 decidedHsk만 결론으로 사용하고,
 *   나머지 잎은 후보로, 주·해설은 분류 근거로, 분류사례·결정례는 참조사례로 매핑한다.
 *   author·generated_at 은 impure(호출 컴포넌트가 병합) — 여기선 코어 필드만.
 *   세율은 모달이 decided_hsk(10자리)로 자동 조회하므로 미포함.
 */
export function buildSearchReportData({ q, tariff, cases, rulings, notes, decidedHsk = "", guide = null }) {
  const items = (tariff && tariff.items) || [];
  const leaves = items.filter(
    (it) => String((it && it.hsk) || "").replace(/\D/g, "").length >= 10,
  );
  const explicitHsk = String(decidedHsk || "").replace(/\D/g, "").slice(0, 10);
  const selected = explicitHsk.length === 10
    ? leaves.find((it) => String(it.hsk || "").replace(/\D/g, "").slice(0, 10) === explicitHsk)
    : null;
  // 관세율표 품명 꼬리 '/양허유형;0' 은 보고서에 싣지 않는다
  const decidedName = selected ? String(selected.desc_ko || selected.desc || "").replace(/\/양허유형;.*$/, "").trim() : "";

  return {
    product_name: q || "",
    decided_hsk: explicitHsk.length === 10 ? explicitHsk : "",
    decided_name: decidedName,
    candidates: leaves.slice(0, 12).map((it) => ({
      hsk: String(it.hsk).replace(/\D/g, "").slice(0, 10),
      reason: it.desc_ko || it.desc || "",
    })),
    evidence: (notes || []).slice(0, 6).map((n) => ({
      label: n.label || "",
      text: cleanText(n.desc, 300),
    })),
    cases: [...(cases || []), ...(rulings || [])].slice(0, 12).map((c) => ({
      ref_no: "",
      hsk: c.hsk || c.hs6 || "",
      gist: c.label || "",
    })),
    // 판정 가이드(PROVE 확정 전): 결정트리 경로·뒷받침/갈림 결정례·가르는 사실·신고 전 확인. 없으면 생략.
    ...(guide ? { guide } : {}),
  };
}
